import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useInventoryOptions, useCreateOption } from '@/hooks/useInventoryOptions';

interface DynamicSelectProps {
  optionType: 'clapbase' | 'categoria' | 'modelo' | 'status' | 'proveedor' | 'techMember' | 'financeMember';
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** false ⇒ sin botón "+" (los FILTROS no deben poder crear taxonomía). */
  allowCreate?: boolean;
  /** true ⇒ item "Todos" que limpia la selección (onValueChange('')). */
  allowClear?: boolean;
}

// El Select de shadcn no acepta value="" como item — sentinel para "Todos"
const CLEAR_SENTINEL = '__all__';

export function DynamicSelect({
  optionType,
  value,
  onValueChange,
  placeholder,
  disabled,
  allowCreate = true,
  allowClear = false,
}: DynamicSelectProps) {
  const { t } = useTranslation();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newValue, setNewValue] = useState('');

  const { data: options = [], isLoading } = useInventoryOptions(optionType);
  const createOption = useCreateOption();

  const handleAddNew = async () => {
    if (!newValue.trim()) return;

    await createOption.mutateAsync({
      optionType,
      value: newValue.trim(),
    });

    // Actualizar el valor seleccionado con la nueva opción
    onValueChange(newValue.trim());

    // Limpiar y cerrar
    setNewValue('');
    setIsAddDialogOpen(false);
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;

    const labels: Record<string, string> = {
      clapbase: 'Seleccionar ubicación',
      categoria: 'Seleccionar categoría',
      modelo: 'Seleccionar modelo',
      status: 'Seleccionar status',
      proveedor: 'Seleccionar proveedor',
      techMember: 'Seleccionar responsable tech',
      financeMember: 'Seleccionar responsable finance',
    };

    return labels[optionType];
  };

  const getDialogTitle = () => {
    const labels: Record<string, string> = {
      clapbase: 'Agregar nueva ubicación',
      categoria: 'Agregar nueva categoría',
      modelo: 'Agregar nuevo modelo',
      status: 'Agregar nuevo status',
      proveedor: 'Agregar nuevo proveedor',
      techMember: 'Agregar miembro de Tech',
      financeMember: 'Agregar miembro de Finance',
    };

    return labels[optionType];
  };

  const getInputPlaceholder = () => {
    const placeholders: Record<string, string> = {
      clapbase: 'ej: Nueva Sede',
      categoria: 'ej: Tablet',
      modelo: 'ej: MacBook Pro M4',
      status: 'ej: En Revisión',
      proveedor: 'ej: Quipteams INTL S.A.S',
      techMember: 'ej: Solange Guerrero',
      financeMember: 'ej: Cecilia Lopez',
    };

    return placeholders[optionType];
  };

  return (
    <>
      <div className="flex gap-2">
        <Select
          value={value || ''}
          onValueChange={(v) => onValueChange(v === CLEAR_SENTINEL ? '' : v)}
          disabled={disabled || isLoading}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={getPlaceholder()} />
          </SelectTrigger>
          <SelectContent>
            {allowClear && (
              <SelectItem value={CLEAR_SENTINEL} className="text-muted-foreground">
                {t('devices.filters_panel.all_option')}
              </SelectItem>
            )}
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {allowCreate && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setIsAddDialogOpen(true)}
            disabled={disabled || isLoading}
            title={t('devices.dynamic_select.add_option')}
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Dialog para agregar nueva opción */}
      {allowCreate && (
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-value">{t('devices.dynamic_select.new_option_label')}</Label>
              <Input
                id="new-value"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={getInputPlaceholder()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNew();
                  }
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                setNewValue('');
              }}
            >
              {t('devices.dynamic_select.cancel')}
            </Button>
            <Button
              onClick={handleAddNew}
              disabled={!newValue.trim() || createOption.isPending}
            >
              {createOption.isPending ? t('devices.dynamic_select.adding') : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </>
  );
}
